import React, { useState, useEffect } from "react";
import {
  CssBaseline,
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  Paper,
  Fade,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useNavigate } from "react-router-dom";
import lightLogo from "../assets/logo.png";
import darkLogo from "../assets/whitelogo.jpeg";

export const apiUrl = process.env.REACT_APP_API_URL;

const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: "#F3F4F6",
      paper: "#FFFFFF",
    },
    primary: {
      main: "#1D4ED8", // Corporate Blue
      dark: "#1E40AF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#3B82F6",
    },
    background: {
      default: "#111827",
      paper: "#1F2937",
    },
    text: {
      primary: "#F9FAFB",
      secondary: "#9CA3AF",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
});

const LoginSignup = ({ onLoginSuccess }) => {
  const [isActive, setIsActive] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    userrole: "",
  });
  const [rememberMe, setRememberMe] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState("light");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const session = JSON.parse(localStorage.getItem("userSession"));
    if (session) {
      const currentTime = Date.now();
      const loginTime = session.loginTime;
      if (currentTime - loginTime >= 20 * 60 * 60 * 1000) {
        logoutUser();
      }
    }
  }, []);

  const validateForm = () => {
    let errors = {};
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email address is invalid";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (isActive) {
      if (!formData.name) errors.name = "Name is required for registration";
      if (!formData.userrole || formData.userrole === "SELECT USER ROLE")
        errors.userrole = "Role is required";
    }

    return errors;
  };

  const handleThemeToggle = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleRegisterClick = () => {
    setIsActive(true);
    setFormErrors({});
    setFormData({ email: "", password: "", name: "", userrole: "" });
  };

  const handleLoginClick = () => {
    setIsActive(false);
    setFormErrors({});
    setFormData({ email: "", password: "", name: "", userrole: "" });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      isActive ? await registerUser() : await loginUser();
    } else {
      setFormErrors(errors);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const registerUser = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        setFormErrors({ email: data.message || "Registration failed" });
        setSnackbar({
          open: true,
          message: data.message || "Registration failed",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Registration successful!",
          severity: "success",
        });
        setFormData({ email: "", password: "", name: "", userrole: "" });
        setIsActive(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "An error occurred. Please try again.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const loginUser = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/user/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setFormErrors({ login: data.message || "Login failed" });
        setSnackbar({
          open: true,
          message: data.message || "Login failed",
          severity: "error",
        });
      } else {
        const { token, user } = data;
        localStorage.setItem(
          "userSession",
          JSON.stringify({
            token,
            user_id: user._id,
            name: user.name,
            email: user.email,
            user_role: user.user_role,
            loginTime: Date.now(),
          })
        );
        localStorage.setItem("isAuthenticated", "true");
        setSnackbar({
          open: true,
          message: "Login successful!",
          severity: "success",
        });
        onLoginSuccess();
        navigate("/dashboard");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "An error occurred. Please try again.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const logoutUser = () => {
    localStorage.removeItem("userSession");
    localStorage.removeItem("isAuthenticated");
    setSnackbar({
      open: true,
      message: "Session expired. Logging out...",
      severity: "info",
    });
    navigate("/");
  };

  return (
    <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "background.default", // Revert to default (Light Gray / Dark Gray)
          position: "relative",
          overflow: "hidden",
          p: 2
        }}
      >
        {/* Subtle Background Elements for Depth */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -100,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: theme === "light"
              ? "radial-gradient(circle, rgba(29, 78, 216, 0.05) 0%, rgba(255,255,255,0) 70%)"
              : "radial-gradient(circle, rgba(29, 78, 216, 0.1) 0%, rgba(0,0,0,0) 70%)",
            filter: "blur(60px)",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            right: -100,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: theme === "light"
              ? "radial-gradient(circle, rgba(29, 78, 216, 0.05) 0%, rgba(255,255,255,0) 70%)"
              : "radial-gradient(circle, rgba(29, 78, 216, 0.1) 0%, rgba(0,0,0,0) 70%)",
            filter: "blur(60px)",
            zIndex: 0,
          }}
        />

        <Fade in={true} timeout={600}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 1000,
              zIndex: 1,
              display: "flex",
              flexDirection: "column", // Stack on mobile
              alignItems: "center",
            }}
          >
            {/* Main Login Card */}
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 1000,
                minHeight: 600,
                display: "flex",
                flexDirection: { xs: "column", md: "row" }, // Row on desktop, column on mobile
                borderRadius: 6,
                overflow: "hidden",
                backgroundColor: "background.paper",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                border: "1px solid #374151",
              }}
            >
              {/* Left Panel: Content & Branding */}
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  background: "#1F2937", // Matches Sidebar background
                  color: "#fff",
                  p: { xs: 4, md: 6 },
                  position: "relative",
                }}
              >
                {/* Overlay Pattern */}
                <Box sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.1,
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '32px 32px',
                }} />

                {/* Left Panel Content */}
                <Box sx={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  {/* Logo */}
                  <img
                    src={darkLogo}
                    alt="Logo"
                    style={{ width: 180 }}
                  />
                  {/* Welcome Text */}
                  <Box>
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                      {isActive ? "Join Us" : "Welcome Back"}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {isActive ? "Create your account to get started." : "Please sign in to access your dashboard."}
                    </Typography>
                  </Box>

                  <Divider sx={{ width: "20%", borderColor: "rgba(255,255,255,0.3)", my: 1 }} />

                  {/* Enterprise CRM Branding */}
                  <Box>
                    <Typography variant="h3" fontWeight={800} sx={{
                      background: "linear-gradient(45deg, #FFFFFF, #93C5FD)",
                      backgroundClip: "text",
                      textFillColor: "transparent",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1
                    }}>
                      Enterprise CRM
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 300, mx: "auto" }}>
                      Manage clients, bookings, and performance with powerful tools designed for enterprise growth.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Right Panel: Form */}
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  p: { xs: 4, md: 6 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  backgroundColor: theme === 'light' ? '#FFFFFF' : '#1F2937'
                }}
              >
                {/* Existing form code here */}
                <form onSubmit={handleSubmit}>
                  {isActive && (
                    <>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        margin="normal"
                        variant="outlined"
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineOutlinedIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Select
                        name="userrole"
                        value={formData.userrole || "SELECT USER ROLE"}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mt: 2, mb: 1 }}
                      >
                        <MenuItem value="SELECT USER ROLE" disabled>
                          Select Role
                        </MenuItem>
                        <MenuItem value="bdm">BDM</MenuItem>
                        <MenuItem value="admin">ADMIN</MenuItem>
                        <MenuItem value="senior admin">SENIOR ADMIN</MenuItem>
                        <MenuItem value="dev">DEV</MenuItem>
                      </Select>
                    </>
                  )}

                  <TextField
                    fullWidth
                    required
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    margin="normal"
                    variant="outlined"
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        transition: "all 0.2s",
                        "&:hover fieldset": { borderColor: "#9CA3AF" },
                        "&.Mui-focused fieldset": { borderColor: "#1D4ED8", borderWidth: "2px" },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    margin="normal"
                    variant="outlined"
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        transition: "all 0.2s",
                        "&:hover fieldset": { borderColor: "#9CA3AF" },
                        "&.Mui-focused fieldset": { borderColor: "#1D4ED8", borderWidth: "2px" },
                      },
                    }}
                  />

                  {/* Additional Login Options */}
                  {!isActive && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={<Typography variant="body2" color="text.secondary">Remember me</Typography>}
                      />
                      <Link href="#" variant="body2" underline="hover" color="primary" onClick={(e) => e.preventDefault()}>
                        Forgot Password?
                      </Link>
                    </Box>
                  )}

                  {formErrors.login && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {formErrors.login}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: "0 4px 6px -1px rgba(29, 78, 216, 0.2), 0 2px 4px -1px rgba(29, 78, 216, 0.1)",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 10px 15px -3px rgba(29, 78, 216, 0.3)",
                      },
                    }}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : isActive ? "Create Account" : "Sign In"}
                  </Button>
                </form>
              </Box>

              {/* <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {isActive ? "Already have an account? " : "Don't have an account? "}
                  <Link
                    component="button"
                    variant="body2"
                    fontWeight="600"
                    underline="hover"
                    onClick={isActive ? handleLoginClick : handleRegisterClick}
                  >
                    {isActive ? "Log In" : "Sign Up"}
                  </Link>
                </Typography>
              </Box> */}
            </Paper>

            {/* Footer / Theme Toggle */}
            <Box sx={{ mt: 4, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} Have Faith Solutions.
              </Typography>
              <IconButton onClick={handleThemeToggle} size="small" sx={{ ml: 1 }}>
                {theme === "light" ? (
                  <DarkModeRoundedIcon fontSize="small" />
                ) : (
                  <LightModeRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
        </Fade>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default LoginSignup;
